import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Pagination } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";
import axios from "axios";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PostAbsentDialog from "./PostAbsentDialog";
import PostMalPracticeDialog from "./PostMalPracticeDialog";
import PostDebarDialog from "./PostDebarDialog";
import StaffAllocationDialog from "./StaffAllocationDialog";
import ShowCourseDialog from "./ShowCourseDialog";
import MarksEntryDialog from "./MarksEntryDialog";
const ShowExamDatesDialog = ({
  open,
  onClose,
  examScheduleMasterId,
  examScheduleDetailId,
  showSnackbar,
  fetchData,
}) => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const user = JSON.parse(sessionStorage.getItem("user"));
  const clientId = user?.client_id;
  const collegeId = user?.college_id;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (open && examScheduleMasterId) {
      axios
        .get(
          `${API_BASE_URL}/api/exam-external-schedule/${clientId}/${collegeId}/${examScheduleMasterId}/${examScheduleDetailId}/dates`
        )
        .then((res) => {
          if (res.data.success) {
            const mapped = res.data.data.map((item, index) => ({ id: index + 1, ...item }));
            setRows(mapped);
            setFilteredRows(mapped);
          }
        })
        .catch((err) => console.error("Error fetching exam dates:", err));
    }
  }, [open, examScheduleMasterId, examScheduleDetailId]);

  const getCourseType = (row) => {
    const type = (row.course_type || "").toString().trim().toUpperCase();

    if (type.startsWith("T")) return "THEORY";
    if (type.startsWith("P")) return "PRACTICAL";

    return "";
  };

  const isMarksUpdated = (row) => {
    const marksCount = Number(row.marks_count || 0);
    const studentCount = Number(row.student_count || 0);

    return studentCount > 0 && marksCount >= studentCount;
  };

  const courseSummary = useMemo(() => {
    const theoryCourses = new Set();
    const practicalCourses = new Set();

    const theoryUpdated = new Set();
    const theoryPending = new Set();

    const practicalUpdated = new Set();
    const practicalPending = new Set();

    rows.forEach((row) => {
      const courseKey = row.exam_course_id || `${row.course_code}_${row.course_name}`;

      const type = (row.course_type || "").toString().trim().toUpperCase();

      const updated = isMarksUpdated(row);

      if (type.startsWith("T")) {
        theoryCourses.add(courseKey);

        if (updated) {
          theoryUpdated.add(courseKey);
        } else {
          theoryPending.add(courseKey);
        }
      }

      if (type.startsWith("P")) {
        practicalCourses.add(courseKey);

        if (updated) {
          practicalUpdated.add(courseKey);
        } else {
          practicalPending.add(courseKey);
        }
      }
    });

    const totalStudents = rows.reduce((sum, row) => sum + Number(row.student_count || 0), 0);

    const totalMarksCount = rows.reduce((sum, row) => sum + Number(row.marks_count || 0), 0);

    const updatedCount = theoryUpdated.size + practicalUpdated.size;

    const pendingCount = theoryPending.size + practicalPending.size;

    return {
      theory: theoryCourses.size,
      practical: practicalCourses.size,
      total: theoryCourses.size + practicalCourses.size,

      updated: updatedCount,
      pending: pendingCount,

      theoryUpdated: theoryUpdated.size,
      theoryPending: theoryPending.size,

      practicalUpdated: practicalUpdated.size,
      practicalPending: practicalPending.size,

      students: totalStudents,
      marksCount: totalMarksCount,
    };
  }, [rows]);
  const columns = useMemo(
    () => [
      { field: "sno", headerName: "S.No", flex: 0.3, minWidth: 60 },
      { field: "program_name", headerName: "Program Name", flex: 1, minWidth: 150 },
      {
        field: "branch_course",
        headerName: "Branch / Course",
        flex: 1,
        minWidth: 400,
        renderCell: (params) => {
          const branch = params.row.branch_name || "";
          const course = params.row.course_name || "";

          return (
            <div
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.5em",
                paddingTop: 6,
                marginTop: 4,
                paddingBottom: 6,
              }}
            >
              {branch}
              {"\n"}
              {course}
            </div>
          );
        },
      },
      {
        field: "course_type",
        headerName: "CourseType",
        flex: 1,
        minWidth: 100,
        valueGetter: (params) =>
          params.value === "T" ? "Theory" : params.value === "P" ? "Practical" : "",
      },
      {
        field: "exam_datetime",
        headerName: "Exam Date / Time",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => {
          const date = params.row.from_exam_date
            ? new Date(params.row.from_exam_date).toLocaleDateString()
            : "";

          const time = params.row.slot_description || "";

          return (
            <div
              style={{
                whiteSpace: "pre-line",
                lineHeight: "1.2em",
                paddingTop: 4,
                marginTop: 4,
                paddingBottom: 4,
              }}
            >
              {date}
              {"\n"}
              {time}
            </div>
          );
        },
      },

      {
        field: "student_count",
        headerName: "Count",
        flex: 0.7,
        minWidth: 100,
        headerClassName: "student-count-header",
        cellClassName: "student-count-cell",
      },
      {
        field: "marks_count",
        headerName: "AB/Marks",
        flex: 0.7,
        minWidth: 100,
        headerClassName: "marks-count-header",
        cellClassName: "marks-count-cell",
      },
      {
        field: "marks_status",
        headerName: "Marks Status",
        flex: 0.9,
        minWidth: 130,
        sortable: false,
        renderCell: (params) => {
          const updated = isMarksUpdated(params.row);

          return (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                fontWeight: "bold",
                fontSize: "13px",
                color: updated ? "#2e7d32" : "#d32f2f",
                backgroundColor: updated ? "rgba(46,125,50,0.10)" : "rgba(211,47,47,0.10)",
                border: `1px solid ${updated ? "#2e7d32" : "#d32f2f"}`,
              }}
            >
              {updated ? "UPDATED" : "PENDING"}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <RowMenu
            row={params.row}
            onGenerateSeatingPlan={handleGenerateSeatingPlan}
            onDownloadBarCodes={onDownloadBarCodes}
            onGenerateAbstractSeatingPlan={onGenerateAbstractSeatingPlan}
            onGenerateRoomCourseStrength={onGenerateRoomCourseStrength}
            onGenerateAttendanceSheet={onGenerateAttendanceSheet}
            onGenerateAwardList={onGenerateAwardList}
            onGenerateMarksList={onGenerateMarksList}
            onGenerateAttendanceList={onGenerateAttendanceList}
            onDownloadBarCodesForStudent={onDownloadBarCodesForStudent}
            onDownloadDaywiseStrength={onDownloadDaywiseStrength}
            examScheduleMasterId={examScheduleMasterId}
            timetableId={params.row.timetable_id}
            program_id={params.row.program_id}
            branch_id={params.row.branch_id}
            program_name={params.row.program_name}
            exam_slot_id={params.row.exam_slot_id}
            exam_type_id={params.row.exam_type_id}
            slot_description={params.row.slot_description}
            from_exam_date={
              params.row.from_exam_date
                ? new Date(params.row.from_exam_date).toISOString().split("T")[0]
                : ""
            }
            showSnackbar={showSnackbar}
            downloadDFormNew={downloadDFormNew}
          />
        ),
      },
    ],
    [examScheduleMasterId, showSnackbar]
  );

  // Search filter
  useEffect(() => {
    if (!searchText) {
      setFilteredRows(rows);
    } else {
      const lower = searchText.toLowerCase();

      setFilteredRows(
        rows.filter((row) => {
          const courseType =
            row.course_type === "T" ? "Theory" : row.course_type === "P" ? "Practical" : "";

          return (
            row.timetable_id?.toString().toLowerCase().includes(lower) ||
            row.program_name?.toLowerCase().includes(lower) ||
            row.branch_name?.toLowerCase().includes(lower) ||
            row.course_name?.toLowerCase().includes(lower) ||
            courseType.toLowerCase().includes(lower) ||
            row.slot_description?.toLowerCase().includes(lower) ||
            row.exam_type_name?.toLowerCase().includes(lower) ||
            new Date(row.from_exam_date).toLocaleDateString().toLowerCase().includes(lower)
          );
        })
      );
    }
  }, [searchText, rows]);

  // Generate Seating Plan + download PDFs
  const handleGenerateSeatingPlan = async (row) => {
    if (!window.confirm("Generate Seating Plan for this exam schedule?")) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/exam-seating-plan/generate-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
        {
          created_by: user.user_email,
        }
      );
      fetchData?.();
      showSnackbar?.("Seating Plan generated successfully", "success");
    } catch (err) {
      console.error(err);
      showSnackbar?.(err?.response?.data?.message || "Seating Plan generation failed", "error");
    }
    // Download all PDFs
    downloadSeatingPlanNew(row);
    //downloadAttendanceNew(row);
    //downloadAbstractSeatingPlanNew(row);
    //downloadDFormNew(row);
  };

  const downloadFile = async (url, row, suffix) => {
    try {
      const response = await axios.get(url, {
        responseType: "blob",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      const fileName = `${suffix}.pdf`.replace(/\s+/g, "_");
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      window.open(blobUrl, `_blank_${fileName}`);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(`Error downloading ${suffix}:`, err);
    }
  };
  const formatDDMMYYYY = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d)) return ""; // invalid date

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
  };
  const onDownloadBarCodes = async (row) => {
    try {
      setLoadingGenerate(true);
      await downloadFile(
        `${API_BASE_URL}/api/exam-seating-plan/barcode-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}/${row.timetable_id}`,
        row,
        `BarCodes_${formatDDMMYYYY(row.from_exam_date)}-${row.program_name}-${row.branch_id}`
      );
    } catch (error) {
      showSnackbar?.("Error generating barcodes", "error");
    } finally {
      setLoadingGenerate(false);
    }
  };
  const onDownloadBarCodesForStudent = async (row, studentRollno) => {
    const rollno = String(studentRollno || "").trim();
    if (!rollno) {
      showSnackbar?.("Student roll number is required", "warning");
      return;
    }
    try {
      setLoadingGenerate(true);
      await downloadFile(
        `${API_BASE_URL}/api/exam-seating-plan/barcode-datewise-student/${clientId}/${collegeId}/${
          row.exam_schedule_master_id
        }/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${
          row.exam_type_id
        }/${row.timetable_id}/${encodeURIComponent(rollno)}`,
        row,
        `BarCodes_${formatDDMMYYYY(row.from_exam_date)}-${rollno}`
      );
    } catch (error) {
      showSnackbar?.("Error generating barcode sheet", "error");
    } finally {
      setLoadingGenerate(false);
    }
  };

  const downloadSeatingPlanNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-seating-plan-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Seating_Plan_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateAttendanceSheet = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendance-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}`,
      row,
      `Attendance_Sheet_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateAwardList = (row) => {
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-awardlist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `AwardList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  };

  const onGenerateMarksList = (row) => {
    if (row.from_exam_date == null) alert("Practical Date is not Assigned");
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-markslist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `MarksList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  };

  const onGenerateAttendanceList = (row) => {
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendancelist-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.course_code}`,
      row,
      `AttendanceList_${row.course_code}-${formatDDMMYYYY(row.from_exam_date)}-${
        row.slot_description
      }`
    );
  };
  const onGenerateAbstractSeatingPlan = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-abstract-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Abstract_Seating_Plan_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );

  const onGenerateRoomCourseStrength = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-room-course-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `Room_Course_Strength_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  const downloadAttendanceNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-attendance-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}`,
      row,
      "Attendance_Sheet"
    );
  const downloadAbstractSeatingPlanNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/download-abstract-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      "Abstract_Seating_Plan"
    );
  const downloadDFormNew = (row) =>
    downloadFile(
      `${API_BASE_URL}/api/exam-seating-plan/dform-datewise/${clientId}/${collegeId}/${row.exam_schedule_master_id}/${row.exam_schedule_detail_id}/${row.from_exam_date}/${row.exam_slot_id}/${row.exam_type_id}`,
      row,
      `DForm_${formatDDMMYYYY(row.from_exam_date)}-${row.slot_description}`
    );
  const onDownloadDaywiseStrength = async (examScheduleMasterId) => {
    try {
      //setLoadingGenerate(true);

      await downloadFile(
        `${API_BASE_URL}/api/exam-seating-plan/daywise-strength/${clientId}/${collegeId}/${examScheduleMasterId}`,
        examScheduleMasterId,
        `Daywise_Strength_Report`
      );
    } catch (error) {
      showSnackbar?.("Error generating Daywise Strength Report", "error");
    } finally {
      //setLoadingGenerate(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }

          onClose();
        }}
        disableEscapeKeyDown
        maxWidth="xxl"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
          }}
        >
          Exam Dates and Actions for Exam Schedule ID: {examScheduleMasterId}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#d32f2f",
              "&:hover": {
                backgroundColor: "#ffebee",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle2" sx={{ mt: 1, color: "red" }}>
            Note: Generate Barcode sheets for each branch. Generate remaining reports date-wise.
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(3, 1fr)",
                md: "repeat(4, 1fr)",
                lg: "repeat(7, 1fr)",
              },
              gap: 1.0,
              mb: 1,
            }}
          >
            {/* THEORY */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #1976d2",
                backgroundColor: "rgba(25,118,210,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#1976d2">
                Theory
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#1976d2">
                {courseSummary.theory}
              </Typography>
              <Typography variant="caption">Courses</Typography>
            </Box>

            {/* PRACTICAL */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #9c27b0",
                backgroundColor: "rgba(156,39,176,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#9c27b0">
                Practical
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#9c27b0">
                {courseSummary.practical}
              </Typography>
              <Typography variant="caption">Courses</Typography>
            </Box>

            {/* TOTAL */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #616161",
                backgroundColor: "rgba(97,97,97,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#616161">
                Total
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#616161">
                {courseSummary.total}
              </Typography>
              <Typography variant="caption">Courses</Typography>
            </Box>

            {/* UPDATED */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #2e7d32",
                backgroundColor: "rgba(46,125,50,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#2e7d32">
                Updated
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                {courseSummary.updated}
              </Typography>
              <Typography variant="caption">Marks</Typography>
            </Box>

            {/* PENDING */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #d32f2f",
                backgroundColor: "rgba(211,47,47,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#d32f2f">
                Pending
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#d32f2f">
                {courseSummary.pending}
              </Typography>
              <Typography variant="caption">Marks</Typography>
            </Box>

            {/* STUDENTS */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #00796b",
                backgroundColor: "rgba(0,121,107,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#00796b">
                Students
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#00796b">
                {courseSummary.students}
              </Typography>
              <Typography variant="caption">Total</Typography>
            </Box>

            {/* AB / SCRIPTS */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #ed6c02",
                backgroundColor: "rgba(237,108,2,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#ed6c02">
                AB/Marks
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#ed6c02">
                {courseSummary.marksCount}
              </Typography>
              <Typography variant="caption">Total</Typography>
            </Box>

            {/* THEORY UPDATED */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #2e7d32",
                backgroundColor: "rgba(46,125,50,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#2e7d32">
                Theory Updated
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                {courseSummary.theoryUpdated}
              </Typography>
              <Typography variant="caption">Marks posted</Typography>
            </Box>

            {/* THEORY PENDING */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #d32f2f",
                backgroundColor: "rgba(211,47,47,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#d32f2f">
                Theory Pending
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#d32f2f">
                {courseSummary.theoryPending}
              </Typography>
              <Typography variant="caption">Marks posted</Typography>
            </Box>

            {/* PRACTICAL UPDATED */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #2e7d32",
                backgroundColor: "rgba(46,125,50,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#2e7d32">
                Practical Updated
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#2e7d32">
                {courseSummary.practicalUpdated}
              </Typography>
              <Typography variant="caption">Marks posted</Typography>
            </Box>

            {/* PRACTICAL PENDING */}
            <Box
              sx={{
                p: 1.0,
                borderRadius: 2,
                textAlign: "center",
                border: "1px solid #d32f2f",
                backgroundColor: "rgba(211,47,47,0.08)",
              }}
            >
              <Typography fontWeight="bold" color="#d32f2f">
                Practical Pending
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="#d32f2f">
                {courseSummary.practicalPending}
              </Typography>
              <Typography variant="caption">Marks posted</Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mt: 1,
              width: { xs: "100%", sm: "50%" },
            }}
          >
            <TextField
              fullWidth
              margin="normal"
              label="Search Exam Date/Branch/CourseName/Type"
              placeholder="Search Exam Date/Branch/CourseName/Type"
              variant="outlined"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: { xs: "100%", sm: "60%" },
                "& .MuiInputBase-root": {
                  height: 40,
                },
              }}
            />
          </Box>

          <Box
            sx={{
              width: "100%",
              overflowX: "auto", // horizontal scroll
              mt: 2,
            }}
          >
            <Box sx={{ minWidth: 650 }}>
              <Pagination
                count={Math.ceil(filteredRows.length / pageSize)}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}
              />
              <DataGrid
                rows={filteredRows
                  .slice((page - 1) * pageSize, page * pageSize)
                  .map((row, index) => ({
                    ...row,
                    id: row.timetable_id || index,
                    sno: (page - 1) * pageSize + index + 1,
                  }))}
                columns={columns}
                autoHeight
                disableRowSelectionOnClick
                /* 🔥 CRITICAL FIX */
                pagination
                paginationMode="server" // ✅ prevents double pagination
                rowCount={filteredRows.length}
                page={page - 1} // DataGrid = 0-based
                onPageChange={(newPage) => setPage(newPage + 1)}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => {
                  setPageSize(newPageSize);
                  setPage(1);
                }}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                onCellClick={(params, event) => event.stopPropagation()}
                onRowClick={(params, event) => event.stopPropagation()}
                sx={{
                  "& .student-count-header": {
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#2e7d32",
                  },
                  "& .student-count-cell": {
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#2e7d32",
                  },

                  "& .marks-count-header": {
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "#d32f2f",
                  },
                  "& .marks-count-cell": {
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: "#d32f2f",
                  },
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={loadingGenerate} onClose={() => setLoadingGenerate(false)}>
        <DialogTitle>Generating Barcodes...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "300px" }}>
            <LinearProgress variant="indeterminate" />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

// RowMenu component
function RowMenu({
  row,
  onGenerateSeatingPlan,
  examScheduleMasterId,
  timetableId,
  program_id,
  branch_id,
  program_name,
  exam_slot_id,
  exam_type_id,
  slot_description,
  from_exam_date,
  showSnackbar,
  downloadDFormNew,
  onDownloadBarCodes,
  onGenerateAbstractSeatingPlan,
  onGenerateRoomCourseStrength,
  onGenerateAttendanceSheet,
  onGenerateAwardList,
  onGenerateMarksList,
  onGenerateAttendanceList,
  onDownloadBarCodesForStudent,
  onDownloadDaywiseStrength,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [postAbsentsOpen, setPostAbsentsOpen] = useState(false);
  const [postMalPracticesOpen, setPostMalPracticesOpen] = useState(false);
  const [invigilators, setInvigilatorsOpen] = useState(false);
  const [postDebarsOpen, setPostDebarsOpen] = useState(false);
  const [barcodeStudentOpen, setBarcodeStudentOpen] = useState(false);
  const [barcodeStudentRollno, setBarcodeStudentRollno] = useState("");
  const [showCourseDialog, setShowCourseDialog] = useState({
    open: false,
    date: null,
    timetable_id: null,
  });
  const [marksEntryOpen, setMarksEntryOpen] = useState(false);
  const [marksCourseRow, setMarksCourseRow] = useState(null);
  const open = Boolean(anchorEl);
  const courseType = (row.course_type || "").toString().trim().toUpperCase();

  const isPractical = courseType.startsWith("P");
  const isTheory = courseType.startsWith("T");
  return (
    <>
      <IconButton size="medium" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        {isPractical ? (
          /* ================= PRACTICAL COURSE MENU ================= */
          <>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAwardList(row);
                onGenerateAttendanceList(row);
              }}
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                backgroundColor: "rgba(25,118,210,0.08)",
                "&:hover": { backgroundColor: "rgba(25,118,210,0.15)" },
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Award & Attendance List
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setMarksCourseRow({
                  ...row,
                  student_rollnos: row.student_rollnos,
                  exam_course_id: row.exam_course_id,
                  year_number: row.year_number,
                  semester_number: row.semester_number,
                  external_minimum: row.external_minimum,
                  external_maximum: row.external_maximum,
                  course_code: row.course_code,
                  course_name: row.course_name,
                });
                setMarksEntryOpen(true);
              }}
              sx={{
                color: "success.main",
                fontWeight: "bold",
                backgroundColor: "rgba(46,125,50,0.08)",
                "&:hover": { backgroundColor: "rgba(46,125,50,0.15)" },
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Marks Entry
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateMarksList(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Practical Marks List
            </MenuItem>
          </>
        ) : (
          /* ================= THEORY COURSE MENU ================= */
          <>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onDownloadBarCodes(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              BarCode Sheets
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setBarcodeStudentRollno("");
                setBarcodeStudentOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              BarCode Sheet(Selected)
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateSeatingPlan(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Seating Plan
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAbstractSeatingPlan(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Abstract Seating Plan
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onDownloadDaywiseStrength(examScheduleMasterId);
              }}
              sx={{
                color: "primary.main",
                fontWeight: "bold",
                backgroundColor: "rgba(25,118,210,0.08)",
                "&:hover": {
                  backgroundColor: "rgba(25,118,210,0.15)",
                },
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Daywise Strength
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateRoomCourseStrength(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Room Course Strength
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                onGenerateAttendanceSheet(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Attendance Sheet
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setInvigilatorsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              View Invigilators
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostAbsentsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post Absents
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostMalPracticesOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post MalPractice
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setPostDebarsOpen(true);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Post Debar
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                downloadDFormNew(row);
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              DForm
            </MenuItem>

            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setShowCourseDialog({
                  open: true,
                  date: row.from_exam_date,
                  timetable_id: row.timetable_id,
                });
              }}
            >
              <ScheduleIcon fontSize="small" sx={{ mr: 1 }} />
              Upload & Allot Scripts
            </MenuItem>
          </>
        )}
      </Menu>

      <ShowCourseDialog
        open={showCourseDialog.open}
        onClose={() => setShowCourseDialog({ open: false })}
        examScheduleMasterId={examScheduleMasterId}
        examDate={showCourseDialog.date}
        timetableId={showCourseDialog.timetable_id}
        programId={program_id}
        branchId={branch_id}
        showSnackbar={showSnackbar}
      />
      <MarksEntryDialog
        open={marksEntryOpen}
        onClose={() => setMarksEntryOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        courseRow={marksCourseRow}
      />
      <StaffAllocationDialog
        open={invigilators}
        onClose={() => setInvigilatorsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        exam_slot_id={exam_slot_id} // ✅ renamed correctly
        exam_slot_desc={slot_description}
        from_exam_date={from_exam_date} // ✅ renamed correctly
        showSnackbar={showSnackbar}
      />
      <PostAbsentDialog
        open={postAbsentsOpen}
        onClose={() => setPostAbsentsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
      <PostMalPracticeDialog
        open={postMalPracticesOpen}
        onClose={() => setPostMalPracticesOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
      <PostDebarDialog
        open={postDebarsOpen}
        onClose={() => setPostDebarsOpen(false)}
        examScheduleMasterId={examScheduleMasterId}
        timetableId={timetableId}
        program_name={program_name}
        exam_slot_id={exam_slot_id}
        exam_type_id={exam_type_id}
        slot_description={slot_description}
        from_exam_date={from_exam_date}
        showSnackbar={showSnackbar}
      />
      <Dialog
        open={barcodeStudentOpen}
        onClose={() => setBarcodeStudentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Download Barcode Sheet</DialogTitle>
        <DialogContent>
          <TextField
            label="Enter student rollnos separated by comma"
            value={barcodeStudentRollno}
            onChange={(e) => setBarcodeStudentRollno(e.target.value)}
            margin="normal"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBarcodeStudentOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#000", color: "#fff", "&:hover": { backgroundColor: "#000" } }}
            onClick={() => {
              onDownloadBarCodesForStudent(row, barcodeStudentRollno);
              setBarcodeStudentOpen(false);
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

RowMenu.propTypes = {
  row: PropTypes.object.isRequired,
  onGenerateSeatingPlan: PropTypes.func.isRequired,
  onDownloadBarCodes: PropTypes.func.isRequired,
  onGenerateAbstractSeatingPlan: PropTypes.func.isRequired,
  onGenerateRoomCourseStrength: PropTypes.func.isRequired,
  onGenerateAttendanceSheet: PropTypes.func.isRequired,
  onGenerateAwardList: PropTypes.func.isRequired,
  onGenerateMarksList: PropTypes.func.isRequired,
  onGenerateAttendanceList: PropTypes.func.isRequired,
  onDownloadBarCodesForStudent: PropTypes.func.isRequired,
  onDownloadDaywiseStrength: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  timetableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  program_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exam_slot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exam_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  slot_description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  from_exam_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSnackbar: PropTypes.func,
  downloadDFormNew: PropTypes.func.isRequired,
};

ShowExamDatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  examScheduleMasterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  examScheduleDetailId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  timetableId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  program_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  branch_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  program_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exam_slot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  exam_type_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  slot_description: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  from_exam_date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showSnackbar: PropTypes.func,
  fetchData: PropTypes.func,
};

export default ShowExamDatesDialog;
